import Badge from '@mui/material/Badge';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {Theme} from '@mui/material/styles';
import React, {memo, ReactNode, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router-dom';

import {SubNavItem} from '../../interfaces/sub-nav-item.interface';

export interface SidebarItemProps {
  item: SubNavItem;
  isOpen: boolean;
  isActive?: boolean;
}

function SidebarItem({item, isOpen, isActive}: SidebarItemProps) {
  const {t} = useTranslation();

  const {to, Icon, label, href, target, counter} = item;

  const renderItem = useCallback(
    (children: ReactNode) => {
      if (to) {
        return (
          <Link to={to} underline="none" component={RouterLink}>
            {children}
          </Link>
        );
      } else if (href) {
        return (
          <Link href={href} target={target} underline="none">
            {children}
          </Link>
        );
      }

      return null;
    },
    [href, target, to]
  );

  const buttonSx = useMemo(
    () => (theme: Theme) => ({
      minHeight: 48,
      px: 2.5,
      justifyContent: isOpen ? 'initial' : 'center',
      ...(isActive
        ? {
            background: `${theme.palette.primary.light} !important`,
            color: `${theme.palette.primary.main} !important`,
            '& svg': {
              fill: theme.palette.primary.main,
              fillOpacity: 1,
            },
          }
        : {}),
    }),
    [isActive, isOpen]
  );

  return renderItem(
    <ListItem key={label} disablePadding sx={{display: 'block'}}>
      <ListItemButton sx={buttonSx}>
        {Icon && (
          <Badge
            sx={theme => ({
              '& .MuiBadge-badge': {
                top: theme.spacing(isOpen ? 1.25 : 0.25),
                right: theme.spacing(isOpen ? -11 : 0),
              },
            })}
            badgeContent={counter}
            color="primary"
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                mr: isOpen ? 3 : 'auto',
              }}
            >
              <Icon />
            </ListItemIcon>
          </Badge>
        )}
        {label && (
          <ListItemText
            primary={t(label)}
            sx={{
              opacity: isOpen ? 1 : 0,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}

export const SidebarItemComponent = memo(SidebarItem);
